import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { DataTablePaginate } from '@/common/ui/table/data-table-paginate'
import { DataTableFilterSelector } from '@/common/ui/table/data-table-selector-with-query'
import { DataTableSimpleSearch } from '@/common/ui/table/data-table-simple-search'
import useMutationProcessPurchase from '@/modules/shop/api/hooks/useMutationProcessPurchase'
import {
	createPurchasesTableColumns,
	REWARD_TYPE_FILTER_OPTIONS,
	CURRENCY_TYPE_FILTER_OPTIONS,
} from './constants'
import { ShopHistoryStatus, type components } from '@/schema'

type Purchase = components['schemas']['AdminShopPurchaseResponse']

interface PurchasesTableProps {
	data: Purchase[]
	total: number
	isLoading?: boolean
}

export default function PurchasesTable({
	data,
	total,
	isLoading,
}: PurchasesTableProps) {
	const navigate = useNavigate()
	const [selectedPurchase, setSelectedPurchase] = useState<Purchase | null>(
		null,
	)
	const [actionType, setActionType] = useState<
		'approve' | 'reject' | 'view' | null
	>(null)

	const { mutate: processPurchase, isPending: isProcessing } =
		useMutationProcessPurchase()

	const handleViewDetails = (id: string) => {
		const purchase = data.find((p) => p.id === id)
		if (purchase) {
			setSelectedPurchase(purchase)
			setActionType('view')
		}
	}

	const handleApprove = (id: string) => {
		const purchase = data.find((p) => p.id === id)
		if (purchase) {
			setSelectedPurchase(purchase)
			setActionType('approve')
		}
	}

	const handleReject = (id: string) => {
		const purchase = data.find((p) => p.id === id)
		if (purchase) {
			setSelectedPurchase(purchase)
			setActionType('reject')
		}
	}

	const handleViewDetailPage = (id: string) => {
		navigate(`/operator/shop/purchases/${id}`)
	}

	const confirmAction = () => {
		if (!selectedPurchase || !actionType) return

		if (actionType === 'approve') {
			processPurchase(
				{
					id: selectedPurchase.id,
					data: { status: ShopHistoryStatus.SUCCESS },
				},
				{ onSuccess: () => closeDialog() },
			)
		} else if (actionType === 'reject') {
			processPurchase(
				{
					id: selectedPurchase.id,
					data: { status: ShopHistoryStatus.FAILED },
				},
				{ onSuccess: () => closeDialog() },
			)
		}
	}

	const closeDialog = () => {
		setSelectedPurchase(null)
		setActionType(null)
	}

	const columns = createPurchasesTableColumns({
		onViewDetails: handleViewDetails,
		onApprove: handleApprove,
		onReject: handleReject,
		onViewDetailPage: handleViewDetailPage,
	})

	return (
		<>
			<div className="space-y-4">
				{/* Filters */}
				<div className="flex flex-wrap items-end gap-4">
					<DataTableSimpleSearch placeHolder="ค้นหาผู้เล่น" queryKey="q" />
					<DataTableFilterSelector
						options={[
							{
								label: 'รอดำเนินการ',
								value: ShopHistoryStatus.PENDING,
							},
							{
								label: 'สำเร็จ',
								value: ShopHistoryStatus.SUCCESS,
							},
							{
								label: 'ล้มเหลว',
								value: ShopHistoryStatus.FAILED,
							},
						]}
						placeholder="เลือกสถานะ"
						filterKey="status"
						label="สถานะ"
						className="w-48"
					/>
					<DataTableFilterSelector
						options={REWARD_TYPE_FILTER_OPTIONS}
						placeholder="เลือกประเภทรางวัล"
						filterKey="rewardType"
						label="ประเภทรางวัล"
						className="w-48"
					/>
					<DataTableFilterSelector
						options={CURRENCY_TYPE_FILTER_OPTIONS}
						placeholder="เลือกประเภทเงินตรา"
						filterKey="currencyType"
						label="ประเภทการจ่าย"
						className="w-48"
					/>
				</div>

				{/* Table */}
				<DataTablePaginate
					columns={columns}
					data={data}
					totalItems={total}
					isLoading={isLoading}
				/>
			</div>

			{/* Action Dialogs */}
			{actionType === 'view' && selectedPurchase && (
				<Dialog open={true} onOpenChange={closeDialog}>
					<DialogContent className="max-w-2xl">
						<DialogHeader>
							<DialogTitle>รายละเอียดคำสั่งซื้อ</DialogTitle>
						</DialogHeader>
						<div className="space-y-4">
							<div className="grid grid-cols-2 gap-4">
								<div>
									<label className="text-sm font-medium text-muted-foreground">
										ผู้เล่น
									</label>
									<p className="text-sm">
										{selectedPurchase.player?.username ||
											selectedPurchase.playerId}
									</p>
								</div>
								<div>
									<label className="text-sm font-medium text-muted-foreground">
										สินค้า
									</label>
									<p className="text-sm">
										{selectedPurchase.item?.name || 'N/A'}
									</p>
								</div>
								<div>
									<label className="text-sm font-medium text-muted-foreground">
										ราคา
									</label>
									<p className="text-sm">
										{selectedPurchase.item.price?.toLocaleString() || 'N/A'}{' '}
										เพชร
									</p>
								</div>
								<div>
									<label className="text-sm font-medium text-muted-foreground">
										จำนวน
									</label>
									<p className="text-sm">
										{selectedPurchase.amount?.toLocaleString() || 'N/A'}
									</p>
								</div>
								<div>
									<label className="text-sm font-medium text-muted-foreground">
										สถานะ
									</label>
									<p className="text-sm">{selectedPurchase.status}</p>
								</div>
								<div>
									<label className="text-sm font-medium text-muted-foreground">
										วันที่สั่งซื้อ
									</label>
									<p className="text-sm">
										{new Date(selectedPurchase.createdAt).toLocaleString(
											'th-TH',
										)}
									</p>
								</div>
								{selectedPurchase.updatedAt &&
									selectedPurchase.updatedAt !== selectedPurchase.createdAt && (
										<div>
											<label className="text-sm font-medium text-muted-foreground">
												วันที่อัพเดทล่าสุด
											</label>
											<p className="text-sm">
												{new Date(selectedPurchase.updatedAt).toLocaleString(
													'th-TH',
												)}
											</p>
										</div>
									)}
								{selectedPurchase.trackingNumber && (
									<div>
										<label className="text-sm font-medium text-muted-foreground">
											หมายเลขติดตาม
										</label>
										<p className="font-mono text-sm">
											{selectedPurchase.trackingNumber}
										</p>
									</div>
								)}
							</div>

							{selectedPurchase.item?.description && (
								<div>
									<label className="text-sm font-medium text-muted-foreground">
										รายละเอียดสินค้า
									</label>
									<p className="text-sm">{selectedPurchase.item.description}</p>
								</div>
							)}

							{selectedPurchase.adminNotes && (
								<div>
									<label className="text-sm font-medium text-muted-foreground">
										หมายเหตุผู้ดูแลระบบ
									</label>
									<p className="rounded bg-muted p-2 text-sm">
										{selectedPurchase.adminNotes}
									</p>
								</div>
							)}

							{selectedPurchase.shippingAddress && (
								<div>
									<label className="text-sm font-medium text-muted-foreground">
										ที่อยู่จัดส่ง
									</label>
									<div className="space-y-1 rounded bg-muted p-2 text-sm">
										{selectedPurchase.shippingAddress.name && (
											<p>
												<strong>ชื่อ:</strong>{' '}
												{selectedPurchase.shippingAddress.name}
											</p>
										)}
										{selectedPurchase.shippingAddress.phone && (
											<p>
												<strong>เบอร์โทร:</strong>{' '}
												{selectedPurchase.shippingAddress.phone}
											</p>
										)}
										{selectedPurchase.shippingAddress.address && (
											<p>
												<strong>ที่อยู่:</strong>{' '}
												{selectedPurchase.shippingAddress.address}
											</p>
										)}
									</div>
								</div>
							)}
						</div>
						<div className="flex justify-end">
							<Button variant="outline" onClick={closeDialog}>
								ปิด
							</Button>
						</div>
					</DialogContent>
				</Dialog>
			)}

			{(actionType === 'approve' || actionType === 'reject') &&
				selectedPurchase && (
					<Dialog open={true} onOpenChange={closeDialog}>
						<DialogContent>
							<DialogHeader>
								<DialogTitle>
									{actionType === 'approve'
										? 'ยืนยันการอนุมัติ'
										: 'ยืนยันการปฏิเสธ'}
								</DialogTitle>
							</DialogHeader>
							<div className="py-4">
								<p>
									คุณแน่ใจหรือไม่ที่จะ
									{actionType === 'approve' ? 'อนุมัติ' : 'ปฏิเสธ'}
									คำสั่งซื้อนี้?
								</p>
								<div className="mt-2 text-sm text-muted-foreground">
									<p>
										ผู้เล่น:{' '}
										{selectedPurchase.player?.username ||
											selectedPurchase.playerId}
									</p>
									<p>สินค้า: {selectedPurchase.item?.name || 'N/A'}</p>
									<p>
										ราคา:{' '}
										{selectedPurchase.item.price?.toLocaleString() || 'N/A'}{' '}
										เพชร
									</p>
								</div>
							</div>
							<div className="flex justify-end gap-2">
								<Button variant="outline" onClick={closeDialog}>
									ยกเลิก
								</Button>
								<Button
									variant={actionType === 'approve' ? 'default' : 'destructive'}
									disabled={isProcessing}
									onClick={confirmAction}
								>
									{isProcessing
										? 'กำลังประมวลผล...'
										: actionType === 'approve'
											? 'อนุมัติ'
											: 'ปฏิเสธ'}
								</Button>
							</div>
						</DialogContent>
					</Dialog>
				)}
		</>
	)
}
